import type { InventoryPackage, TransactionItem } from '@/api/types';

// ─── Urgency ──────────────────────────────────

export type Urgency = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export function assignUrgency(daysOnHand: number): Urgency {
  if (daysOnHand < 3) {
    return 'CRITICAL';
  }
  if (daysOnHand < 7) {
    return 'HIGH';
  }
  if (daysOnHand < 14) {
    return 'MEDIUM';
  }
  return 'LOW';
}

// ─── Product Velocity ─────────────────────────

export interface ProductVelocity {
  product_id: number;
  product_name: string;
  sku: string | null;
  brand_name: string | null;
  brand_catalog_brand_id: string | null;
  quantity_on_hand: number;
  unit_id: number;
  unit_abbreviation: string | null;
  unit_cost: number | null;
  daily_velocity: number;
  projected_days_on_hand: number;
  urgency: Urgency;
  category: string | null;
  low_inventory_threshold: number | null;
}

/**
 * Aggregates inventory packages by ProductId, computes velocity from
 * transaction history, and assigns urgency based on projected days-on-hand.
 */
export function computeProductVelocities(
  inventory: InventoryPackage[],
  transactions: TransactionItem[],
  lookbackDays: number,
): ProductVelocity[] {
  // Aggregate on-hand by ProductId (multiple packages per product must be summed)
  const onHandMap = new Map<
    number,
    {
      product_name: string;
      sku: string | null;
      brand_name: string | null;
      brand_catalog_brand_id: string | null;
      unit_id: number;
      unit_abbreviation: string | null;
      unit_cost: number | null;
      quantity: number;
      category: string | null;
      low_inventory_threshold: number | null;
    }
  >();

  for (const item of inventory) {
    if (!item.product) {
      continue;
    }

    const productId = item.product.id;
    const existing = onHandMap.get(productId);
    if (existing) {
      existing.quantity += item.quantity ?? 0;
    } else {
      onHandMap.set(productId, {
        product_name: item.product.whseProductsDescription ?? `Product ${productId}`,
        sku: item.product.whseProductsProductNo ?? null,
        brand_name: item.product.brand?.brandName ?? null,
        brand_catalog_brand_id: item.product.brand?.brandCatalogBrandId ?? null,
        unit_id: item.unit?.unitId ?? 0,
        unit_abbreviation: item.unit?.abbreviation ?? null,
        unit_cost: item.product.unitCost ?? null,
        quantity: item.quantity ?? 0,
        category: item.product.productTypeNavigation?.productType ?? null,
        low_inventory_threshold: item.product.lowInventoryThreshold ?? null,
      });
    }
  }

  // Aggregate sold units from transactions
  const soldMap = new Map<number, number>();
  for (const tx of transactions) {
    soldMap.set(tx.ProductId, (soldMap.get(tx.ProductId) ?? 0) + tx.Quantity);
  }

  // Compute velocity and days-on-hand
  const results: ProductVelocity[] = [];
  for (const [productId, info] of onHandMap) {
    const totalSold = soldMap.get(productId) ?? 0;
    const dailyVelocity = totalSold / lookbackDays;
    const daysOnHand = dailyVelocity > 0 ? info.quantity / dailyVelocity : Infinity;

    results.push({
      product_id: productId,
      product_name: info.product_name,
      sku: info.sku,
      brand_name: info.brand_name,
      brand_catalog_brand_id: info.brand_catalog_brand_id,
      quantity_on_hand: info.quantity,
      unit_id: info.unit_id,
      unit_abbreviation: info.unit_abbreviation,
      unit_cost: info.unit_cost,
      daily_velocity: Math.round(dailyVelocity * 100) / 100,
      projected_days_on_hand: daysOnHand === Infinity ? 9999 : Math.round(daysOnHand * 10) / 10,
      urgency: daysOnHand === Infinity ? 'LOW' : assignUrgency(daysOnHand),
      category: info.category,
      low_inventory_threshold: info.low_inventory_threshold,
    });
  }

  // Sort ascending by projected_days_on_hand (most urgent first)
  results.sort((a, b) => a.projected_days_on_hand - b.projected_days_on_hand);
  return results;
}
